"use client";

import { Check } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Link } from "@/components/ui/link";

import { CATEGORIES, STRINGS } from "./strings";

/** One answer per question: yes, no, or not yet answered. */
type Answer = boolean | null;

const TOTAL = CATEGORIES.reduce((n, c) => n + c.questions.length, 0);

interface CategoryScore {
  name: string;
  yes: number;
  total: number;
  answered: number;
}

export function ReadinessScorecard() {
  // answers[categoryIndex][questionIndex]
  const [answers, setAnswers] = useState<Answer[][]>(() =>
    CATEGORIES.map((c) => c.questions.map(() => null))
  );

  function setAnswer(ci: number, qi: number, value: boolean) {
    setAnswers((prev) =>
      prev.map((cat, i) =>
        i === ci
          ? // Tapping the active option again clears it back to unanswered.
            cat.map((a, j) => (j === qi ? (a === value ? null : value) : a))
          : cat
      )
    );
  }

  const { scores, yesTotal, answeredTotal } = useMemo(() => {
    let yesTotal = 0;
    let answeredTotal = 0;
    const scores: CategoryScore[] = CATEGORIES.map((cat, ci) => {
      const catAnswers = answers[ci] ?? [];
      const yes = catAnswers.filter((a) => a === true).length;
      const answered = catAnswers.filter((a) => a !== null).length;
      yesTotal += yes;
      answeredTotal += answered;
      return { name: cat.name, yes, total: cat.questions.length, answered };
    });
    return { scores, yesTotal, answeredTotal };
  }, [answers]);

  // The weakest fully-answered category with gaps - flagged as "Start here".
  // Only surfaces once every question has an answer, so the advice is based on
  // the full picture rather than a partial one.
  const weakestName = useMemo(() => {
    if (answeredTotal !== TOTAL) return null;
    const withGaps = scores.filter((c) => c.yes < c.total);
    if (withGaps.length === 0) return null;
    return withGaps.reduce((a, b) =>
      b.yes / b.total < a.yes / a.total ? b : a
    ).name;
  }, [scores, answeredTotal]);

  // Fire one analytics event ~800ms after the user finishes the checklist.
  // Individual answers are intentionally not sent - only the aggregate score.
  const posthog = usePostHog();
  useEffect(() => {
    if (answeredTotal !== TOTAL) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "readiness-scorecard",
        score: yesTotal,
        total: TOTAL,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, answeredTotal, yesTotal]);

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <div className="grid gap-6 lg:grid-cols-[1fr_28rem] lg:gap-10">
        {/* Questions */}
        <div>
          <h2 className="text-heading-16 mb-5 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>
          <div className="flex flex-col gap-6">
            {CATEGORIES.map((cat, ci) => {
              const catAnswers = answers[ci] ?? [];
              const yes = scores[ci]?.yes ?? 0;
              return (
                <div key={cat.name}>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-label-12 font-bold tracking-wide text-[#0D0D0D] uppercase">
                      {cat.name}
                    </span>
                    <span className="text-body-14 text-grey-400 font-semibold">
                      {yes} of {cat.questions.length}
                    </span>
                  </div>
                  <div>
                    {cat.questions.map((q, qi) => {
                      const answer = catAnswers[qi] ?? null;
                      return (
                        <div
                          key={q.text}
                          className="border-grey-100 flex items-center justify-between gap-4 border-b py-3 last:border-b-0"
                        >
                          <div className="min-w-0">
                            <p className="text-body-14 text-[#0D0D0D]">
                              {q.text}
                            </p>
                            {q.link && (
                              // Always rendered so the row height stays fixed;
                              // toggled with `invisible` (keeps its space, drops
                              // out of tab order and the a11y tree) so the Yes/No
                              // buttons don't jump when the answer changes.
                              <Link
                                href={q.link.href}
                                aria-hidden={answer === true}
                                className={`text-pulse-700 mt-1 inline-block text-[13px] leading-tight no-underline hover:underline focus-visible:underline ${
                                  answer === true ? "invisible" : ""
                                }`}
                              >
                                <span aria-hidden="true">&rarr; </span>
                                {q.link.text}
                              </Link>
                            )}
                          </div>
                          <YesNoToggle
                            question={q.text}
                            value={answer}
                            onChange={(value) => setAnswer(ci, qi, value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Result panel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div aria-live="polite" className="bg-pulse-50 rounded-xl p-5 sm:p-6">
            <Headline
              answeredTotal={answeredTotal}
              yesTotal={yesTotal}
              weakestName={weakestName}
            />

            <div className="bg-pulse-100 mt-5 h-2 overflow-hidden rounded-full">
              <div
                className="bg-pulse-700 h-full rounded-full transition-[width] duration-300 ease-out"
                style={{ width: `${(yesTotal / TOTAL) * 100}%` }}
              />
            </div>

            <dl className="border-pulse-100 mt-5 border-t">
              {scores.map((c) => {
                const complete = c.yes === c.total;
                return (
                  <div
                    key={c.name}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <dt className="text-body-14 text-[#3f4453]">{c.name}</dt>
                    <dd className="text-body-14 flex items-center gap-2 font-bold text-[#0D0D0D]">
                      {c.name === weakestName && (
                        <span className="text-label-12 bg-pulse-100 text-pulse-700 rounded-full px-2 py-0.5 font-bold tracking-wide uppercase">
                          {STRINGS.startHere}
                        </span>
                      )}
                      {c.yes}/{c.total}
                      {complete && (
                        <Check
                          className="size-4 text-[#15803d]"
                          aria-hidden="true"
                        />
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>

            <ResultNote answeredTotal={answeredTotal} yesTotal={yesTotal} />
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-5 w-auto shrink-0 md:h-6 md:w-auto"
        />
      </div>
    </div>
  );
}

function Headline({
  answeredTotal,
  yesTotal,
  weakestName,
}: {
  answeredTotal: number;
  yesTotal: number;
  weakestName: string | null;
}) {
  const className =
    "text-heading-24 lg:text-heading-32 text-[#0D0D0D] leading-tight";

  if (answeredTotal === 0) {
    return <p className={className}>{STRINGS.resultPrompt}</p>;
  }

  if (yesTotal === TOTAL) {
    return (
      <p className={className}>
        All{" "}
        <span className="text-pulse-700">
          {TOTAL} of {TOTAL}
        </span>{" "}
        in place. You&rsquo;re set for the big weekend.
      </p>
    );
  }

  const tail =
    answeredTotal === TOTAL && weakestName ? (
      <>
        {" "}
        Your biggest gap is{" "}
        <strong>{weakestName.toLowerCase()} preparation</strong>.
      </>
    ) : answeredTotal < TOTAL ? (
      <> Keep going to see the full picture.</>
    ) : null;

  return (
    <p className={className}>
      You have{" "}
      <span className="text-pulse-700">
        {yesTotal} of {TOTAL}
      </span>{" "}
      in place.{tail}
    </p>
  );
}

function ResultNote({
  answeredTotal,
  yesTotal,
}: {
  answeredTotal: number;
  yesTotal: number;
}) {
  if (yesTotal === TOTAL) {
    return (
      <p className="text-body-14 mt-4 leading-relaxed text-[#3f4453]">
        <span className="font-bold text-[#15803d]">Ready.</span> Every box is
        ticked. Use the run-up to test your sequences and brief your team.
      </p>
    );
  }

  if (answeredTotal === TOTAL) {
    const remaining = TOTAL - yesTotal;
    return (
      <p className="text-body-14 mt-4 leading-relaxed text-[#3f4453]">
        <strong>
          {remaining} {remaining === 1 ? "item" : "items"} still open.
        </strong>{" "}
        Start with the flagged area - it has the most ground to make up before
        the sale.
      </p>
    );
  }

  return null;
}

function YesNoToggle({
  question,
  value,
  onChange,
}: {
  question: string;
  value: Answer;
  onChange: (value: boolean) => void;
}) {
  return (
    <div
      role="group"
      aria-label={question}
      className="border-grey-100 flex shrink-0 overflow-hidden rounded-full border"
    >
      <button
        type="button"
        aria-pressed={value === true}
        onClick={() => onChange(true)}
        className={`text-body-14 focus-visible:outline-pulse-700 inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center px-4 font-semibold transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 ${
          value === true
            ? "bg-pulse-700 text-white"
            : "text-grey-400 bg-white hover:text-[#0D0D0D]"
        }`}
      >
        Yes
      </button>
      <button
        type="button"
        aria-pressed={value === false}
        onClick={() => onChange(false)}
        className={`text-body-14 focus-visible:outline-pulse-700 inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center px-4 font-semibold transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 ${
          value === false
            ? "bg-[#0D0D0D] text-white"
            : "text-grey-400 bg-white hover:text-[#0D0D0D]"
        }`}
      >
        No
      </button>
    </div>
  );
}
